import { createContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from 'react-modal';
import classes from "./App.module.scss";
import alertIcon from "./assets/akar-icons_alert.svg";
import correctIcon from "./assets/akar-icons_correct.svg";
import { BACKEND_BASE_URL, SUPPORT_EMAIL} from "./constants"
import { impersonateUser } from "./components/ApiService";
import {
  FORGOT_PASSWORD_PATH,
  LOGIN_PATH,
  RESET_PATH,
  VERIFY_PATH,
} from "./routes";

const IMPERSONATION_ACTIVE_KEY = "impersonation-active";
const IMPERSONATION_TARGET_KEY = "impersonation-target";
const ADMIN_SESSION_BACKUP_KEY = "admin-session-backup";
const TERMS_ACCEPTED_KEY = "terms-accepted";

function readStoredTermsAccepted() {
  try {
    return JSON.parse(localStorage.getItem(TERMS_ACCEPTED_KEY)) === true;
  } catch (error) {
    return false;
  }
}


function readQueryParam(name) {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch (error) {
    return null;
  }
}

// Password login is one request (validate + email the MFA code). Show
// "Signing in…" first, then "Sending code…" after this delay so a fast
// incorrect-password response never looks like the code was already sent.
const LOGIN_SENDING_CODE_DELAY_MS = 2000;

const ValidUserContext = createContext({
  isLoggedIn: false,
  isLoggingIn: false,
  loginStatus: "",
  isForgotPwd: false,
  isNewUser: false,
  pwdResetToken: "",
  mfaRequired: false,
  sessionEpoch: 0,
  isImpersonating: false,
  impersonationTarget: "",
  termsAccepted: false,
  apiAuthCheck: (enteredEmail, enteredPassword, reuseJwt) => {},
  apiVerifyMfa: (code) => {},
  clearMfa: () => {},
  localAuthCheck: (needRefresh) => {},
  refreshTableauEmbedToken: async () => false,
  acceptTerms: async () => {},
  startImpersonation: async (email) => {},
  exitImpersonation: async () => {},
  forgotPassword: () => {},
  reset: () => {}
});

export const ValidUserContextProvider = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginStatus, setLoginStatus] = useState("");
  const [isForgotPwd, setIsForgotPwd] = useState(false);
  // Capture reset/new-user tokens on first paint so App can show ResetForm
  // immediately (setState-during-render was racing with the login form).
  const [pwdResetTokenValue, setPwdResetTokenValue] = useState(
    () => readQueryParam("reset") || ""
  );
  const [newUserTokenValue, setNewUserTokenValue] = useState(
    () => readQueryParam("newuser") || ""
  );
  const [unSubscribeUserValue, setUnSubscribeUserValue] = useState(
    () => readQueryParam("unsubscribe") || ""
  );
  const [mfaChallengeId, setMfaChallengeId] = useState("");
  const [mfaPendingEmail, setMfaPendingEmail] = useState("");
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const [isImpersonating, setIsImpersonating] = useState(
    () => localStorage.getItem(IMPERSONATION_ACTIVE_KEY) === "true"
  );
  const [impersonationTarget, setImpersonationTarget] = useState(
    () => localStorage.getItem(IMPERSONATION_TARGET_KEY) || ""
  );
  const [termsAccepted, setTermsAccepted] = useState(readStoredTermsAccepted);

  const [modalIsOpen, setIsOpen] = useState(false);
  const [modalText, setModalText] = useState('Error');
  const [modalIcon, setModalIcon] = useState(alertIcon);
  // When false, dismiss the modal without navigating away (keeps MFA challenge state).
  const [modalNavigateOnClose, setModalNavigateOnClose] = useState(true);

  function openModal(text, isError, autoDismiss, navigateOnClose = true) {
    if (isError) {
      setModalIcon(alertIcon);
    } else {
      setModalIcon(correctIcon);
    }
    setModalText(text);
    setModalNavigateOnClose(navigateOnClose);
    setIsOpen(true);
    if (autoDismiss) {
      setTimeout(() => {
        closeModal();
      }, "4000");
    }
  }

  function afterOpenModal() {
    // references are now sync'd and can be accessed.
  }

  function closeModal() {
    setIsOpen(false);
    if (!modalNavigateOnClose) {
      return;
    }
    setIsForgotPwd(false);
    setPwdResetTokenValue("");
    setNewUserTokenValue("");
    navigate(LOGIN_PATH, { replace: true });
  }

  function removeLoginData() {
    if (JSON.parse(localStorage.getItem("remember-me")) ? true : false) {
      localStorage.removeItem("login-data");      
    } else {
      sessionStorage.removeItem("login-data");  
    }
  }

  function setLoginData(jwt) {
    if (JSON.parse(localStorage.getItem("remember-me")) ? true : false) {
      localStorage.setItem("login-data", jwt);  
    } else {
      sessionStorage.setItem("login-data", jwt); 
    }
  }

  function getLoginData() {
    if (JSON.parse(localStorage.getItem("remember-me")) ? true : false) {
      return JSON.parse(localStorage.getItem("login-data"))
    } else {
      return JSON.parse(sessionStorage.getItem("login-data"))
    }
  }

  function applySessionPayload(data, { updateTableauJwt = true, markLoggedIn = true, loginEmail } = {}) {
    setLoginData(JSON.stringify(data.app_jwt));
    if (updateTableauJwt) {
      localStorage.setItem("tableau-login-data", JSON.stringify(data.tableau_jwt));
    }
    localStorage.setItem("dashboard-url", JSON.stringify(data.dashboard_url));
    localStorage.setItem("navigation", JSON.stringify(data.navigation));
    localStorage.setItem("group", JSON.stringify(data.group));
    localStorage.setItem(
      "groups",
      JSON.stringify(data.groups || (data.group ? [data.group] : []))
    );
    localStorage.setItem("user-name", JSON.stringify(data.full_user_name));
    localStorage.setItem("company", JSON.stringify(data.company));
    localStorage.setItem("client", JSON.stringify(data.client));
    localStorage.setItem("company_logo", JSON.stringify(data.company_logo));
    localStorage.setItem("client_logo", JSON.stringify(data.client_logo));
    localStorage.setItem("ms_download_url", JSON.stringify(data.ms_download_url));
    localStorage.setItem("client_list", JSON.stringify(data.client_list));
    localStorage.setItem("folder-aliases", JSON.stringify(data.folder_aliases || {}));
    localStorage.setItem("folder-priorities", JSON.stringify(data.folder_priorities || {}));
    localStorage.setItem("role", JSON.stringify(data.role));
    const accepted = Boolean(data.terms_accepted);
    localStorage.setItem(TERMS_ACCEPTED_KEY, JSON.stringify(accepted));
    setTermsAccepted(accepted);
    if (loginEmail) {
      localStorage.setItem("login-name", String(loginEmail).trim().toLowerCase());
    }
    if (markLoggedIn) {
      setIsLoggedIn(data.app_jwt);
    }
  }

  function clearImpersonationState() {
    localStorage.removeItem(IMPERSONATION_ACTIVE_KEY);
    localStorage.removeItem(IMPERSONATION_TARGET_KEY);
    localStorage.removeItem(ADMIN_SESSION_BACKUP_KEY);
    setIsImpersonating(false);
    setImpersonationTarget("");
  }

  const customStyles = {
    overlay: {
        backgroundColor: 'rgba(255, 255, 255, 0.20)'
    },
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      backgroundColor: 'white',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center'
    },
  };


  // When landing from an email link, force logged-out and strip the token from the URL
  // so it is not left in history / leaked via Referer on later navigations.
  useEffect(() => {
    if (!pwdResetTokenValue && !newUserTokenValue && !unSubscribeUserValue) {
      return;
    }
    setIsLoggedIn(false);
    removeLoginData();
    localStorage.removeItem("dashboard-url");
    localStorage.removeItem("tableau-login-data");
    localStorage.removeItem(TERMS_ACCEPTED_KEY);
    setTermsAccepted(false);
    if (pwdResetTokenValue || newUserTokenValue) {
      navigate(RESET_PATH, { replace: true });
    } else {
      navigate(
        { pathname: location.pathname || "/", search: "", hash: location.hash || "" },
        { replace: true }
      );
    }
    // Run once on boot with the token captured from the initial query string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function sendPwdResetHandler(username) {
    const url = BACKEND_BASE_URL+"/send_reset_email";
    var myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    var raw = JSON.stringify(
        {
          "useremail":username
        });

    var requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    };
    setIsLoggingIn(true)
    await fetch(url, requestOptions)
      .then((response) => {
        if (response.ok) {
          openModal("Look for an email from Conference Direct to reset your password. If you don’t hear from us momentarily, please check your spam folder. \n Thank you!", false, true);
          setIsLoggingIn(false)
        } else {
          setIsLoggingIn(false)
          openModal('Reset Email Failed', true, false);
        }
      })
      .catch((e) => {
        setIsLoggingIn(false)
        openModal('Reset Email Failed', true, false);
      });
  }

  async function apiPwdResetHandler(enteredPassword, displayName) {
    const url = BACKEND_BASE_URL+"/reset";
    var myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    
    var base64Url = (newUserTokenValue || pwdResetTokenValue).split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    var jwt_values= JSON.parse(jsonPayload);

    var raw = JSON.stringify(
        {
          "username":jwt_values.username,
          "displayname": displayName,
          "password":enteredPassword,
          "token": newUserTokenValue || pwdResetTokenValue
        });

    var requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    };
    setIsLoggingIn(true)
    await fetch(url, requestOptions)
      .then((response) => {
        if (response.ok) {
          openModal('Password Updated', false, true);
          setIsForgotPwd(false);
          setPwdResetTokenValue("");
          setNewUserTokenValue("");
          setIsLoggingIn(false);
        } else {
          openModal('Update Password Failed, please request again a new password reset email', true, false);
          setIsForgotPwd(false);
          setIsLoggingIn(false)
        }
      })
      .catch((e) => {
        openModal('Update Password Failed, please request again a new password reset email', true, false);
        setIsForgotPwd(false);
        setIsLoggingIn(false)
      });
  }

  async function apiAuthCheckHandler(enteredEmail, enteredPassword, reuseJwt) {
    var url =BACKEND_BASE_URL+"/login";
    if (reuseJwt) {
      url =BACKEND_BASE_URL+"/login_refresh";
    }


    var myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    if (reuseJwt) {
      myHeaders.append("Auth-Token", getLoginData());
    }
    var raw = JSON.stringify({"useremail":enteredEmail,"password":enteredPassword, "rememberme":true});

    var requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    };

    setIsLoggingIn(true);
    let sendingCodeTimer = null;
    if (!reuseJwt) {
      setLoginStatus("signing_in");
      sendingCodeTimer = setTimeout(() => {
        setLoginStatus("sending_code");
      }, LOGIN_SENDING_CODE_DELAY_MS);
    }

    const finishLoginRequest = () => {
      if (sendingCodeTimer) {
        clearTimeout(sendingCodeTimer);
      }
      setLoginStatus("");
      setIsLoggingIn(false);
    };

    await fetch(url, requestOptions)
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data.mfa_required && data.challenge_id) {
          setMfaChallengeId(data.challenge_id);
          setMfaPendingEmail(
            (data.useremail || enteredEmail || "").trim().toLowerCase()
          );
          navigate(VERIFY_PATH, { state: location.state });
        } else if (data.app_jwt !== undefined) {
          setMfaChallengeId("");
          setMfaPendingEmail("");
          if (!reuseJwt) {
            clearImpersonationState();
          }
          applySessionPayload(data, {
            updateTableauJwt: !reuseJwt,
            markLoggedIn: true,
            loginEmail: enteredEmail,
          });
        } else {
          openModal(data.error, true, false);
        }
        finishLoginRequest();
      })
      .catch((e) => {
        finishLoginRequest();
        removeLoginData();
        localStorage.removeItem("dashboard-url");
        localStorage.removeItem("tableau-login-data");
        openModal(`System error, please contact ${SUPPORT_EMAIL} if the issue persists`, true, false);
      });
  }

  async function apiVerifyMfaHandler(code) {
    const url = BACKEND_BASE_URL + "/login/mfa";
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    const raw = JSON.stringify({
      useremail: mfaPendingEmail,
      challenge_id: mfaChallengeId,
      code: (code || "").trim(),
    });
    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    setIsLoggingIn(true);
    await fetch(url, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        if (data.app_jwt !== undefined) {
          setMfaChallengeId("");
          setMfaPendingEmail("");
          clearImpersonationState();
          applySessionPayload(data, {
            updateTableauJwt: true,
            markLoggedIn: true,
            loginEmail: mfaPendingEmail,
          });
        } else {
          // Stay on MFA form so the user can retry without losing the challenge.
          openModal(data.error || "Invalid verification code", true, false, false);
        }
        setIsLoggingIn(false);
      })
      .catch(() => {
        setIsLoggingIn(false);
        openModal(
          `System error, please contact ${SUPPORT_EMAIL} if the issue persists`,
          true,
          false,
          false
        );
      });
  }

  const clearMfaHandler = () => {
    setMfaChallengeId("");
    setMfaPendingEmail("");
    navigate(LOGIN_PATH);
  };


  async function apiAuthTimeoutHandler(enteredEmail, enteredPassword, reuseJwt) {
    var url =BACKEND_BASE_URL+"/login";
    if (reuseJwt) {
      url =BACKEND_BASE_URL+"/login_refresh";
    }

    var myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    if (reuseJwt) {
      myHeaders.append("Auth-Token", getLoginData());
    }
    var raw = JSON.stringify({"useremail":enteredEmail,"password":enteredPassword, "rememberme":true});

    var requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    };

    await fetch(url, requestOptions)
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data.app_jwt !== undefined) {
          applySessionPayload(data, {
            updateTableauJwt: false,
            markLoggedIn: false,
            loginEmail: enteredEmail,
          });
        }
      })
      .catch((e) => {
        setIsLoggingIn(false)
        removeLoginData();
        localStorage.removeItem("dashboard-url");
        localStorage.removeItem("tableau-login-data");
      });
  }

  async function startImpersonationHandler(userEmail) {
    const target = (userEmail || "").trim().toLowerCase();
    if (!target) {
      throw new Error("User email is required");
    }
    if (isImpersonating) {
      throw new Error("Already impersonating a user");
    }

    const adminJwt = getLoginData();
    const adminLoginName = localStorage.getItem("login-name") || "";
    if (!adminJwt) {
      throw new Error("Admin session not found");
    }

    setIsLoggingIn(true);
    try {
      const data = await impersonateUser(target);
      if (!data || data.app_jwt === undefined) {
        throw new Error(data?.error || "Impersonation failed");
      }

      localStorage.setItem(
        ADMIN_SESSION_BACKUP_KEY,
        JSON.stringify({ loginData: adminJwt, loginName: adminLoginName })
      );
      localStorage.setItem(IMPERSONATION_ACTIVE_KEY, "true");
      localStorage.setItem(IMPERSONATION_TARGET_KEY, target);
      localStorage.setItem("login-name", target);
      applySessionPayload(data, { updateTableauJwt: true, markLoggedIn: true });
      setIsImpersonating(true);
      setImpersonationTarget(target);
      setSessionEpoch((epoch) => epoch + 1);
    } finally {
      setIsLoggingIn(false);
    }
  }

  async function exitImpersonationHandler() {
    const rawBackup = localStorage.getItem(ADMIN_SESSION_BACKUP_KEY);
    if (!rawBackup) {
      clearImpersonationState();
      throw new Error("Admin session backup not found");
    }

    let backup;
    try {
      backup = JSON.parse(rawBackup);
    } catch (e) {
      clearImpersonationState();
      throw new Error("Admin session backup is invalid");
    }

    const adminJwt = backup.loginData;
    let adminLoginName = backup.loginName || "";
    if (!adminJwt) {
      clearImpersonationState();
      throw new Error("Admin session backup is incomplete");
    }
    if (!adminLoginName) {
      try {
        const payload = JSON.parse(
          decodeURIComponent(
            window
              .atob(adminJwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
              .split("")
              .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
              .join("")
          )
        );
        adminLoginName = payload.useremail || "";
      } catch (e) {
        // fall through; refresh will fail with a clear error
      }
    }
    if (!adminLoginName) {
      throw new Error("Admin login name not found in session backup");
    }

    setIsLoggingIn(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Auth-Token", adminJwt);
      const raw = JSON.stringify({
        useremail: adminLoginName,
        password: "",
        rememberme: true,
      });

      const response = await fetch(`${BACKEND_BASE_URL}/login_refresh`, {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.app_jwt === undefined) {
        throw new Error(data.error || "Could not restore admin session");
      }

      if (adminLoginName) {
        localStorage.setItem("login-name", adminLoginName);
      }
      applySessionPayload(data, { updateTableauJwt: true, markLoggedIn: true });
      clearImpersonationState();
      setSessionEpoch((epoch) => epoch + 1);
    } finally {
      setIsLoggingIn(false);
    }
  }

  async function refreshTableauEmbedTokenHandler() {
    const localData = getLoginData();
    if (!localData) {
      logoutUserHandler("embed_refresh_no_jwt");
      return false;
    }

    let useremail = "";
    try {
      const base64Url = localData.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      useremail = JSON.parse(jsonPayload).useremail || "";
    } catch (e) {
      logoutUserHandler("embed_refresh_invalid_jwt");
      return false;
    }

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Auth-Token", localData);
    const raw = JSON.stringify({
      useremail,
      password: "",
      rememberme: true,
    });

    try {
      const response = await fetch(`${BACKEND_BASE_URL}/login_refresh`, {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      });
      if (response.status === 401) {
        logoutUserHandler("embed_refresh_unauthorized");
        return false;
      }
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.app_jwt === undefined || !data.tableau_jwt) {
        console.warn("Tableau embed token refresh failed:", response.status);
        openModal(
          `Could not refresh the dashboard session. Please try again. Contact ${SUPPORT_EMAIL} if the issue persists`,
          true,
          false,
          false
        );
        return false;
      }
      applySessionPayload(data, { updateTableauJwt: true, markLoggedIn: false });
      return true;
    } catch (e) {
      console.warn("Tableau embed token refresh network error", e);
      openModal(
        `Could not refresh the dashboard session. Please try again. Contact ${SUPPORT_EMAIL} if the issue persists`,
        true,
        false,
        false
      );
      return false;
    }
  }

  const localAuthCheckHandler = (needRefresh) => {
    if (pwdResetTokenValue || newUserTokenValue) {
      setIsLoggedIn(false);
      return false;
    }
    const localData = getLoginData();
    if (localData !== null) {
      var base64Url = localData.split('.')[1];
      var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      var jwt_values= JSON.parse(jsonPayload);
      if (Date.now() >= jwt_values.exp * 1000) {
        removeLoginData();
        localStorage.removeItem("dashboard-url");
        localStorage.removeItem("tableau-login-data");
        clearImpersonationState();
        setIsLoggedIn(false);
        console.warn("Logout: jwt_expired");
        return false;
      } else {
        if (needRefresh) {
          if (Date.now() >= jwt_values.shrt_exp * 1000) {
            console.log("Refresh tokens after short expiration")
            apiAuthCheckHandler(jwt_values.useremail, "", true)
          } else {
            console.log("Refresh tokens after inteval")
            //apiAuthTimeoutHandler(jwt_values.useremail, "", true)
            setIsLoggedIn(true);
          }
          return
        }
      }
    } else {
      setIsLoggedIn(false);
      clearImpersonationState();
      console.log("Set logged out no local data");
    }
  };

  const logoutUserHandler = (reason = "explicit") => {
    console.warn("Logout:", reason);
    removeLoginData();
    localStorage.removeItem("dashboard-url");
    localStorage.removeItem("tableau-login-data");
    localStorage.removeItem("navigation");
    localStorage.removeItem("group");
    localStorage.removeItem("groups");
    localStorage.removeItem("user-name");
    localStorage.removeItem("company");
    localStorage.removeItem("client");
    localStorage.removeItem("ms_download_url");
    localStorage.removeItem("role");
    localStorage.removeItem("folder-aliases");
    localStorage.removeItem("folder-priorities");
    localStorage.removeItem(TERMS_ACCEPTED_KEY);
    setTermsAccepted(false);
    clearImpersonationState();

    setIsLoggedIn(false);
    navigate(LOGIN_PATH, { replace: true });
  };

  async function acceptTermsHandler() {
    const localData = getLoginData();
    if (!localData) {
      throw new Error("Your session expired. Please sign in again.");
    }
    const response = await fetch(`${BACKEND_BASE_URL}/user/terms`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Auth-Token": localData,
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || "Could not save your acknowledgment.");
    }
    localStorage.setItem(TERMS_ACCEPTED_KEY, JSON.stringify(true));
    setTermsAccepted(true);
  }

  const forgotPasswordHandler = () => {
    setIsForgotPwd(true);
    navigate(FORGOT_PASSWORD_PATH);
  };

  const resetHandler = () => {
    setIsForgotPwd(false);
    setPwdResetTokenValue("");
    setNewUserTokenValue("");
    setUnSubscribeUserValue("");
    setMfaChallengeId("");
    setMfaPendingEmail("");
    navigate(LOGIN_PATH);
  };


  const context = {
    isLoggedIn: isLoggedIn,
    isLoggingIn: isLoggingIn,
    loginStatus: loginStatus,
    isForgotPwd: isForgotPwd,
    pwdResetTokenValue: pwdResetTokenValue,
    newUserTokenValue: newUserTokenValue,
    unSubscribeUserValue: unSubscribeUserValue,
    mfaRequired: Boolean(mfaChallengeId),
    sessionEpoch: sessionEpoch,
    isImpersonating: isImpersonating,
    impersonationTarget: impersonationTarget,
    termsAccepted: termsAccepted,
    apiAuthCheck: apiAuthCheckHandler,
    apiVerifyMfa: apiVerifyMfaHandler,
    clearMfa: clearMfaHandler,
    localAuthCheck: localAuthCheckHandler,
    refreshTableauEmbedToken: refreshTableauEmbedTokenHandler,
    acceptTerms: acceptTermsHandler,
    startImpersonation: startImpersonationHandler,
    exitImpersonation: exitImpersonationHandler,
    logoutUser: logoutUserHandler,
    apiPwdReset: apiPwdResetHandler,
    apiSendPwdResetHandler: sendPwdResetHandler,
    forgotPassword: forgotPasswordHandler,
    reset: resetHandler
  };

  return (
    <ValidUserContext.Provider value={context}>
      {props.children}
      <Modal
        isOpen={modalIsOpen}
        onAfterOpen={afterOpenModal}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Example Modal"
      >
        <img
          className={classes.alerticon}
          src={modalIcon}
          alt="Password icon"
          htmlFor="user-password"
        ></img>
        <div className={classes.modaltext}>{modalText}</div>
      </Modal>
    </ValidUserContext.Provider>
  );
};

export default ValidUserContext;
